import React, { useState } from 'react';
import {
  TextField,
  Box,
  Typography,
  Button,
  Divider,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import { Add, Send } from '@mui/icons-material';
import questionnairesService from '../../../services/questionnairesService';

const QuestionariesForm = () => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [questionnaireId, setQuestionnaireId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [isCreatingHead, setIsCreatingHead] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const MAX_QUESTIONS = 15;

  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const handleHeadSubmit = async () => {
    if (!title || !description || questionnaireId || isCreatingHead) return;

    try {
      setIsCreatingHead(true);
      const user = JSON.parse(localStorage.getItem('usuarioConCedula'));

      const response = await questionnairesService.createQuestionnaireHead({
        title,
        description,
        uploadedBy: user.id,
      });

      const questionnaireData = response.data || response;

      if (questionnaireData?.id) {
        setQuestionnaireId(questionnaireData.id);
        showToast('✅ Cuestionario creado exitosamente', 'success');
      } else {
        throw new Error('La respuesta no contiene un ID válido');
      }
    } catch (error) {
      console.error(error);
      showToast('❌ Error al crear el cuestionario', 'error');
    } finally {
      setIsCreatingHead(false);
    }
  };

  const generateQuestions = () => {
    if (!questionnaireId || questions.length > 0) return;

    const newQuestions = [];

    for (let i = 0; i < MAX_QUESTIONS; i++) {
      newQuestions.push({
        questionText: '',
        optionA: '',
        optionB: '',
        optionC: '',
        optionD: '',
        correctAnswer: '',
        difficulty: i < 5 ? 0 : i < 10 ? 1 : 2, // 5 fáciles, 5 medias, 5 difíciles
      });
    }

    setQuestions(newQuestions);
  };

  const handleQuestionChange = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = value;
    setQuestions(updated);
  };

  const handleSubmitAllQuestions = async () => {
    if (!questionnaireId) {
      showToast('⚠️ Primero debes crear el cuestionario', 'warning');
      return;
    }
  
    const incomplete = questions.find(q =>
      !q.questionText ||
      !q.optionA ||
      !q.optionB ||
      !q.optionC ||
      !q.optionD ||
      !q.correctAnswer
    );
  
    if (incomplete) {
      showToast('⚠️ Todas las preguntas deben estar completas', 'warning');
      return;
    }
  
    try {
      setIsSubmitting(true);
  
      for (const question of questions) {
        await questionnairesService.createQuestionarieQuestion(questionnaireId, question);
      }
  
      showToast('✅ Todas las preguntas enviadas correctamente', 'success');
    } catch (error) {
      console.error('Error al enviar preguntas:', error);
      showToast('❌ Error al enviar las preguntas', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };
  

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <TextField
        label="Título"
        variant="outlined"
        fullWidth
        required
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onBlur={handleHeadSubmit}
        disabled={!!questionnaireId}
      />

      <TextField
        label="Descripción"
        variant="outlined"
        fullWidth
        multiline
        rows={3}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        onBlur={handleHeadSubmit}
        disabled={!!questionnaireId}
      />

      {questionnaireId && (
        <Typography variant="body2" color="success.main">
          ✅ Cuestionario creado con ID: {questionnaireId}
        </Typography>
      )}

      <Divider />

      {questionnaireId && questions.length === 0 && (
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={generateQuestions}
        >
          Generar 15 preguntas
        </Button>
      )}

      {questions.map((q, index) => {
        const dificultad = ['Fácil', 'Media', 'Difícil'][q.difficulty];
        const bgColors = ['#d4edda', '#fff3cd', '#f8d7da']; // verde claro, amarillo claro, rojo claro
        const borderColors = ['#28a745', '#ffc107', '#dc3545'];

        return (
          <Box
            key={index}
            display="flex"
            flexDirection="column"
            gap={1}
            p={2}
            mb={2}
            sx={{
              backgroundColor: bgColors[q.difficulty],
              borderLeft: `6px solid ${borderColors[q.difficulty]}`,
              borderRadius: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: borderColors[q.difficulty],
                fontWeight: 'bold',
              }}
            >
              Pregunta {index + 1} — {dificultad}
            </Typography>

            <TextField
              label="Texto de la pregunta"
              value={q.questionText}
              onChange={(e) => handleQuestionChange(index, 'questionText', e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Opción A"
              value={q.optionA}
              onChange={(e) => handleQuestionChange(index, 'optionA', e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Opción B"
              value={q.optionB}
              onChange={(e) => handleQuestionChange(index, 'optionB', e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Opción C"
              value={q.optionC}
              onChange={(e) => handleQuestionChange(index, 'optionC', e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Opción D"
              value={q.optionD}
              onChange={(e) => handleQuestionChange(index, 'optionD', e.target.value)}
              fullWidth
              required
            />
            <TextField
              select
              label="Respuesta correcta"
              value={q.correctAnswer}
              onChange={(e) => handleQuestionChange(index, 'correctAnswer', e.target.value)}
              fullWidth
              required
            >
              <MenuItem value="A">Opción A</MenuItem>
              <MenuItem value="B">Opción B</MenuItem>
              <MenuItem value="C">Opción C</MenuItem>
              <MenuItem value="D">Opción D</MenuItem>
            </TextField>
          </Box>
        );
      })}

      {questions.length === MAX_QUESTIONS && (
        <Button
          variant="contained"
          color="primary"
          endIcon={<Send />}
          onClick={handleSubmitAllQuestions}
          disabled={isSubmitting}
        >
          Enviar todas las preguntas
        </Button>
      )}

      <Snackbar 
        open={toast.open} 
        autoHideDuration={4000} 
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseToast} 
          severity={toast.severity} 
          sx={{ width: '100%' }}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuestionariesForm;
